import { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Sidebar } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { Skeleton } from '@/components/ui/Skeleton'
import { useClearFiltersOnModuleChange } from '@/hooks/useClearFiltersOnModuleChange'

/**
 * Persistent dashboard shell: collapsible sidebar + topbar, with nested routes
 * rendered into the outlet. Screens are lazy-loaded, so the outlet has its own
 * Suspense boundary with a skeleton — never a spinner.
 */
export function DashboardShell() {
  const { pathname } = useLocation()
  useClearFiltersOnModuleChange()

  return (
    // `relative overflow-hidden`: <main> is the only scroller. Any absolutely
    // positioned descendant (sr-only inputs, popovers) is contained here instead
    // of escaping to the document and giving the window a second scrollbar.
    <div className="relative flex h-full overflow-hidden print:block print:h-auto print:overflow-visible">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col print:block">
        <Topbar />
        {/* `relative` on purpose: it makes `main` the containing block for the
            absolutely-positioned `sr-only` elements screens render (the
            Select's hidden native <select>, Stepper labels, …). Without it
            those resolve against the document, and one sitting below the fold
            inflated the body's scroll height — a second, page-level scrollbar
            beside this one (seen on the hotel wizard and listing, 2026-09-16). */}
        <main className="relative min-w-0 flex-1 overflow-y-auto p-4 lg:p-6 print:overflow-visible print:p-0">
          <Suspense
            fallback={
              <div className="space-y-4">
                <Skeleton className="h-6 w-48" label="Loading screen" />
                <Skeleton variant="table" rows={8} cols={6} />
              </div>
            }
          >
            {/* A screen crash is scoped to its route: navigating elsewhere
                clears it instead of showing the error on every page until a
                full reload (seen 2026-10-01). */}
            <ErrorBoundary scope="screen" resetKey={pathname}>
              <Outlet />
            </ErrorBoundary>
          </Suspense>
        </main>
      </div>
    </div>
  )
}
